import { useState, useEffect, useRef, useMemo, useCallback } from "react";

import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";

import { useAuth } from "@/lib/auth/AuthProvider";
import { isAdult, MIN_AGE } from "@/lib/auth/age";
import PhoneAuthDialog from "@/components/auth/PhoneAuthDialog";
import BrandPreviewPanel from "@/components/auth/BrandPreviewPanel";
import { toast } from "sonner";
import { APPS, type AppMeta } from "@/lib/apps";
import { trackEvent } from "@/lib/analytics/track";

/* ── Community colors (one per wave / dot) ── */
const COMMUNITIES = [
  { name: "Fryqnc",     color: "#A78BFA" },  // violet
  { name: "Pulsynq",    color: "#F59E0B" },  // amber
  { name: "MindLynq",   color: "#22D3EE" },  // cyan
  { name: "Spiritsynq", color: "#D97706" },  // warm ochre
  { name: "Jadesynq",   color: "#10B981" },  // emerald
  { name: "Deafwav",    color: "#3B82F6" },  // blue
  { name: "Ablelynq",   color: "#14B8A6" },  // teal
  { name: "Fullynq",    color: "#F43F5E" },  // rose
  { name: "Openlynq",   color: "#C084FC" },  // purple
] as const;

const SIGNAL_GRADIENT = COMMUNITIES.map((c) => c.color).join(", ");

/* ── Shared wave tuning (mutable refs so canvas reads live values) ── */
const defaultTuning = { opacity: 0.7, speedMul: 1, freqMul: 1 };
const waveTuning = { ...defaultTuning };

/* ── Animated waveform canvas ── */
const WaveCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const smoothRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    let reducedMotion = mq.matches;
    const onMotionChange = (e: MediaQueryListEvent) => { reducedMotion = e.matches; };
    mq.addEventListener("change", onMotionChange);

    let t = 0;
    let animId: number;

    const onMouseMove = (e: MouseEvent) => {
      if (reducedMotion) return;
      mouseRef.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouseRef.current.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener("mousemove", onMouseMove, { passive: true });

    const resize = () => {
      canvas.width = canvas.offsetWidth * (window.devicePixelRatio || 1);
      canvas.height = canvas.offsetHeight * (window.devicePixelRatio || 1);
      ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
    };
    resize();
    window.addEventListener("resize", resize);

    const baseConfigs = [
      { freq: 0.006, speed: 0.8,  amp: 30, thickness: 2.2 },
      { freq: 0.010, speed: 1.4,  amp: 22, thickness: 2.0 },
      { freq: 0.015, speed: 2.0,  amp: 18, thickness: 1.8 },
      { freq: 0.008, speed: 1.0,  amp: 35, thickness: 2.4 },
      { freq: 0.012, speed: 1.6,  amp: 20, thickness: 1.6 },
      { freq: 0.007, speed: 0.6,  amp: 40, thickness: 2.6 },
      { freq: 0.018, speed: 2.4,  amp: 14, thickness: 1.4 },
      { freq: 0.009, speed: 1.2,  amp: 26, thickness: 2.0 },
      { freq: 0.014, speed: 1.8,  amp: 16, thickness: 1.6 },
    ];

    const drawFrame = () => {
      const W = canvas.offsetWidth;
      const H = canvas.offsetHeight;
      ctx.setTransform(window.devicePixelRatio || 1, 0, 0, window.devicePixelRatio || 1, 0, 0);
      ctx.clearRect(0, 0, W, H);

      smoothRef.current.x += (mouseRef.current.x - smoothRef.current.x) * 0.04;
      smoothRef.current.y += (mouseRef.current.y - smoothRef.current.y) * 0.04;
      const mx = smoothRef.current.x;
      const my = smoothRef.current.y;

      const sMul = waveTuning.speedMul;
      const fMul = waveTuning.freqMul;

      COMMUNITIES.forEach(({ color }, i) => {
        const cfg = baseConfigs[i];
        const freq = cfg.freq * fMul;
        const speed = cfg.speed * sMul;
        const phase = (i / COMMUNITIES.length) * Math.PI * 2;
        const depth = 0.5 + (i / (COMMUNITIES.length - 1)) * 0.5;
        const px = mx * 18 * depth;
        const py = my * 10 * depth;
        const yBase = H * 0.52 + (i / (COMMUNITIES.length - 1)) * (H * 0.44) + py;

        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = cfg.thickness;
        ctx.globalAlpha = reducedMotion
          ? 0.4
          : 0.38 + 0.15 * Math.sin(t * 0.4 + phase);

        for (let x = 0; x <= W; x += 2) {
          const y =
            yBase +
            cfg.amp * Math.sin((x + px) * freq + t * speed + phase) +
            cfg.amp * 0.35 * Math.sin((x + px) * freq * 2.5 + t * speed * 0.6 + phase * 1.3);
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      });

      ctx.globalAlpha = 1;
    };

    const loop = () => {
      canvas.style.opacity = String(waveTuning.opacity);
      drawFrame();
      const speedFactor = reducedMotion ? 0.25 : 1;
      t += 0.018 * waveTuning.speedMul * speedFactor;
      animId = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      mq.removeEventListener("change", onMotionChange);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: waveTuning.opacity }}
      aria-hidden="true"
    />
  );
};

/* ── Main Auth page ── */
const Auth = () => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";

  const contextApp: AppMeta | null = useMemo(() => {
    const slug = next.replace(/^\//, "").split("/")[0];
    return APPS.find((a) => a.id === slug) ?? null;
  }, [next]);

  useEffect(() => {
    trackEvent("auth_page_view", { app: contextApp?.id ?? null });
  }, [contextApp]);

  const [phoneAuthEnabled, setPhoneAuthEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("feature_flags")
        .select("enabled")
        .eq("key", "phone_auth_enabled")
        .maybeSingle();
      if (!cancelled) setPhoneAuthEnabled(Boolean(data?.enabled));
    })();
    return () => { cancelled = true; };
  }, []);

  const [tab, setTab] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [legalFirstName, setLegalFirstName] = useState("");
  const [legalLastName, setLegalLastName] = useState("");
  const [dob, setDob] = useState("");
  const [busy, setBusy] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [magicMode, setMagicMode] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  useEffect(() => {
    if (session) navigate(next, { replace: true });
  }, [session, navigate, next]);

  const AUTH_ORIGIN = "https://www.wavlynq.com";

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdult(dob)) {
      toast.error(`You must be ${MIN_AGE}+ to join Wavlynq.`);
      return;
    }
    if (!legalFirstName.trim() || !legalLastName.trim()) {
      toast.error("Legal first and last name are required.");
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${AUTH_ORIGIN}${next}`,
        data: {
          legal_first_name: legalFirstName.trim(),
          legal_last_name: legalLastName.trim(),
          dob,
        },
      },
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    trackEvent("auth_signup_complete", { app: contextApp?.id ?? null });
    toast.success("If that email is new, a confirmation link is on its way. Check your inbox (and spam).");
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (error) toast.error(error.message);
    else trackEvent("auth_signin_complete", { app: contextApp?.id ?? null });
  };

  const friendlyOAuthError = useCallback(
    (provider: "google" | "apple", err: { message?: string; code?: string; status?: number } | null | undefined): string => {
      const label = provider === "apple" ? "Apple" : "Google";
      const raw = (err?.message ?? "").toLowerCase();
      if (raw.includes("popup") && raw.includes("closed")) return `${label} sign-in was cancelled. Tap the button again to retry.`;
      if (raw.includes("popup") && raw.includes("block")) return `Your browser blocked the ${label} sign-in window. Allow pop-ups for this site and try again.`;
      if (raw.includes("network") || raw.includes("failed to fetch")) return `Network error reaching ${label}. Check your connection and try again.`;
      return `Couldn't sign in with ${label}. Please try again or use another method.`;
    },
    [],
  );

  const handleOAuth = async (provider: "google" | "apple") => {
    setBusy(true);
    try {
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: `${AUTH_ORIGIN}/auth/callback`,
        extraParams: provider === "google" ? { prompt: "select_account" } : undefined,
      });

      if (result.error) {
        console.error(`[auth] ${provider} OAuth error`, result.error);
        toast.error(friendlyOAuthError(provider, result.error));
        setBusy(false);
        return;
      }

      if (result.redirected) return;
      navigate(next, { replace: true });
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error(`[auth] ${provider} OAuth threw`, e);
      toast.error(friendlyOAuthError(provider, err));
      setBusy(false);
    }
  };

  const handleForgot = async () => {
    if (!email) return toast.error("Enter your email first.");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${AUTH_ORIGIN}/reset-password`,
    });
    if (error) toast.error(error.message);
    else toast.success("If that email exists, a reset link is on its way.");
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your email first.");
    setBusy(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${AUTH_ORIGIN}/auth/callback?next=${encodeURIComponent(next)}` },
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    setMagicSent(true);
    toast.success("Check your email for a sign-in link.");
  };

  /* ── Styles (inline to keep it self-contained) ── */
  const s = {
    page: {
      display: "flex",
      minHeight: "100vh",
      background: "#f8f6f2",
      fontFamily: "'DM Sans', sans-serif",
      color: "#1a1824",
    } as React.CSSProperties,
    left: {
      flex: 1,
      minWidth: 0,
      position: "relative" as const,
      overflow: "hidden",
      padding: "40px 48px",
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "center",
    } as React.CSSProperties,
    center: {
      width: 420,
      minWidth: 420,
      maxWidth: 420,
      background: "#fff",
      padding: "48px 40px",
      borderLeft: "1px solid #ede9e2",
      borderRight: "1px solid #ede9e2",
      position: "relative" as const,
      display: "flex",
      flexDirection: "column" as const,
      overflowY: "auto" as const,
    } as React.CSSProperties,
    right: {
      flex: 1,
      minWidth: 0,
      position: "relative" as const,
      overflow: "hidden",
      padding: "40px 48px",
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "center",
      alignItems: "center",
    } as React.CSSProperties,
    logo: { fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 32, letterSpacing: 1, color: "#1a1824", zIndex: 2, position: "absolute" as const, top: 40, left: 48 } as React.CSSProperties,
    logoQ: { color: "#7c5fe6" },
    heroWrap: { zIndex: 2, position: "relative" as const } as React.CSSProperties,
    eyebrow: { fontWeight: 500, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" as const, color: "#aaaaaa", marginBottom: 12 } as React.CSSProperties,
    headline: { fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: "clamp(22px, 2.2vw, 34px)", lineHeight: 1.15, color: "#1a1824", marginBottom: 16, maxWidth: 380 } as React.CSSProperties,
    headlineEm: { fontWeight: 400, fontStyle: "italic", color: "#7c5fe6" },
    subtext: { fontWeight: 300, fontSize: 14, color: "#888888", lineHeight: 1.7, maxWidth: 300, marginBottom: 0 } as React.CSSProperties,
    pills: { display: "flex", flexWrap: "wrap" as const, gap: 10, marginTop: 28, maxWidth: 340, justifyContent: "center" } as React.CSSProperties,
    pill: { background: "#fff", border: "1px solid #e8e4de", borderRadius: 999, padding: "7px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 500, color: "#666666", whiteSpace: "nowrap" as const } as React.CSSProperties,
    dot: (c: string) => ({ width: 9, height: 9, borderRadius: "50%", background: c, flexShrink: 0 }) as React.CSSProperties,
    // center panel
    accentBar: { position: "absolute" as const, top: 0, left: 0, width: 2, height: "100%", background: "linear-gradient(180deg, transparent, #a57ff040, #4db8a040, #e8a84040, transparent)" } as React.CSSProperties,
    signalBar: { position: "absolute" as const, bottom: 0, left: 0, right: 0, height: 2.5, background: `linear-gradient(90deg, ${SIGNAL_GRADIENT}, ${COMMUNITIES[0].color})`, backgroundSize: "200%", animation: "auth-signal-shift 4s linear infinite" } as React.CSSProperties,
    formEyebrow: { fontWeight: 500, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase" as const, color: "#bbbbbb", marginBottom: 10 } as React.CSSProperties,
    heading: { fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 28, color: "#1a1824", marginBottom: 4 } as React.CSSProperties,
    subheading: { fontSize: 13, color: "#aaaaaa", marginBottom: 28 } as React.CSSProperties,
    tabContainer: { background: "#f4f1ec", borderRadius: 10, padding: 4, display: "flex", marginBottom: 28 } as React.CSSProperties,
    tabBtn: (active: boolean) => ({
      flex: 1,
      textAlign: "center" as const,
      padding: "9px 0",
      borderRadius: 7,
      fontFamily: "'DM Sans', sans-serif",
      fontWeight: 500,
      fontSize: 13,
      cursor: "pointer",
      border: "none",
      transition: "all 0.2s",
      background: active ? "#7c5fe6" : "transparent",
      color: active ? "#ffffff" : "#bbbbbb",
    }) as React.CSSProperties,
    label: { display: "block", fontWeight: 500, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" as const, color: "#aaaaaa", marginBottom: 6 } as React.CSSProperties,
    input: { width: "100%", background: "#f8f6f2", border: "1.5px solid #e8e4de", borderRadius: 9, padding: "12px 14px", color: "#1a1824", fontFamily: "'DM Sans', sans-serif", fontSize: 14, marginBottom: 16, outline: "none", transition: "border-color 0.2s, background 0.2s", boxSizing: "border-box" as const } as React.CSSProperties,
    cta: { width: "100%", background: "#7c5fe6", color: "#fff", border: "none", borderRadius: 9, padding: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 14, letterSpacing: "0.01em", cursor: "pointer", marginBottom: 18, transition: "background 0.2s" } as React.CSSProperties,
    divider: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 } as React.CSSProperties,
    dividerLine: { flex: 1, height: 1, background: "#ede9e2" } as React.CSSProperties,
    dividerText: { fontSize: 11, color: "#cccccc" },
    socialGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 24 } as React.CSSProperties,
    socialBtn: { background: "#fff", border: "1.5px solid #e8e4de", color: "#555555", borderRadius: 9, padding: 11, fontFamily: "'DM Sans', sans-serif", fontSize: 13, cursor: "pointer", transition: "border-color 0.2s" } as React.CSSProperties,
    legal: { fontSize: 10, color: "#cccccc", lineHeight: 1.6 } as React.CSSProperties,
    legalLink: { color: "#7c5fe6", textDecoration: "none" },
    forgotRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: -4, marginBottom: 22, fontSize: 12 } as React.CSSProperties,
    rightQuote: { fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontStyle: "italic", fontSize: "clamp(18px, 1.8vw, 26px)", lineHeight: 1.5, color: "#999999", textAlign: "center" as const, maxWidth: 300, zIndex: 2, position: "relative" as const } as React.CSSProperties,
    rightAttrib: { fontSize: 12, color: "#bbbbbb", marginTop: 12, textAlign: "center" as const, zIndex: 2, position: "relative" as const } as React.CSSProperties,
  };

  const isSignUp = tab === "signup";

  return (
    <>
      {/* Fonts + Keyframes */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&display=swap');
        @keyframes auth-signal-shift {
          0% { background-position: 0% }
          100% { background-position: 200% }
        }
        .auth-input:focus {
          border-color: #7c5fe6 !important;
          background: #fff !important;
        }
        .auth-input::placeholder { color: #cccccc; }
        .auth-cta:hover { background: #6a4fd4 !important; }
        .auth-cta:active { transform: scale(0.99); }
        .auth-social:hover { border-color: #1a1824 !important; }
        @media (max-width: 767px) {
          .auth-left-panel, .auth-right-panel { display: none !important; }
          .auth-center-panel {
            width: 100% !important;
            min-width: unset !important;
            border-left: none !important;
            border-right: none !important;
            min-height: 100vh;
          }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .auth-right-panel { display: none !important; }
          .auth-left-panel { flex: 1 !important; }
          .auth-center-panel {
            width: 400px !important;
            min-width: 400px !important;
          }
        }
      `}</style>

      <div style={s.page}>
        {/* ── LEFT PANEL ── */}
        <div className="auth-left-panel" style={s.left}>
          <WaveCanvas />
          <div style={s.logo}>
            wavlyn<span style={s.logoQ}>q</span>
          </div>
          <div style={s.heroWrap}>
            <p style={s.eyebrow}>Many signals. One connection.</p>
            <h2 style={s.headline}>
              Nine communities.{"\n"}One place where{" "}
              <em style={s.headlineEm}>you belong.</em>
            </h2>
            <p style={s.subtext}>
              Identity-first connection built with safety, dignity, and real community at the center.
              Operated by Young's Digital Atelier.
            </p>
          </div>
        </div>

        {/* ── CENTER PANEL (Sign-in form) ── */}
        <div className="auth-center-panel" style={s.center}>
          <div style={s.accentBar} />
          <div style={s.signalBar} />

          {/* Mobile logo (hidden on desktop via left panel visibility) */}
          <div className="auth-mobile-logo" style={{ display: "none", marginBottom: 24, fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 24, color: "#1a1824" }}>
            wavlyn<span style={{ color: "#7c5fe6" }}>q</span>
          </div>
          <style>{`@media (max-width: 767px) { .auth-mobile-logo { display: block !important; } }`}</style>

          <div style={{ flex: 1 }}>
            <p style={s.formEyebrow}>
              {contextApp ? `${contextApp.community} · 18+` : "WAVLYNQ · 18+"}
            </p>
            <h1 style={s.heading}>
              {contextApp ? `Join ${contextApp.name}` : "Welcome"}
            </h1>
            <p style={s.subheading}>
              Sign in or create your account to join the network.
            </p>

            {/* Tab toggle */}
            <div style={s.tabContainer}>
              <button style={s.tabBtn(tab === "signin")} onClick={() => setTab("signin")}>Sign in</button>
              <button style={s.tabBtn(tab === "signup")} onClick={() => setTab("signup")}>Sign up</button>
            </div>

            {/* ── SIGN IN ── */}
            {!isSignUp && !magicMode && (
              <form onSubmit={handleSignIn}>
                <label style={s.label} htmlFor="signin-email">Email</label>
                <input className="auth-input" style={s.input} id="signin-email" type="email" required placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} />

                <label style={s.label} htmlFor="signin-pw">Password</label>
                <input className="auth-input" style={s.input} id="signin-pw" type="password" required placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)} />

                <div style={s.forgotRow}>
                  <button type="button" onClick={() => setMagicMode(true)}
                    style={{ background: "none", border: "none", color: "#cccccc", cursor: "pointer", fontSize: 12, fontFamily: "'DM Sans', sans-serif", padding: 0 }}>
                    Email me a sign-in link
                  </button>
                  <button type="button" onClick={handleForgot}
                    style={{ background: "none", border: "none", color: "#7c5fe6", cursor: "pointer", fontSize: 12, fontFamily: "'DM Sans', sans-serif", padding: 0, textDecoration: "none" }}>
                    Forgot password?
                  </button>
                </div>

                <button className="auth-cta" type="submit" disabled={busy} style={s.cta}>
                  {busy ? "Signing in…" : "Sign in"}
                </button>
              </form>
            )}

            {/* ── MAGIC LINK ── */}
            {!isSignUp && magicMode && (
              magicSent ? (
                <div style={{ border: "1px solid #e8e4de", borderRadius: 9, padding: 24, textAlign: "center", marginBottom: 18 }}>
                  <p style={{ fontWeight: 500, fontSize: 14, marginBottom: 8 }}>Check your email</p>
                  <p style={{ fontSize: 12, color: "#888888", marginBottom: 12 }}>
                    We sent a one-time sign-in link to <strong>{email}</strong>. It expires in 1 hour.
                  </p>
                  <button type="button" onClick={() => { setMagicSent(false); setMagicMode(false); }}
                    style={{ background: "none", border: "none", color: "#7c5fe6", cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>
                    Use a different email
                  </button>
                </div>
              ) : (
                <form onSubmit={handleMagicLink}>
                  <label style={s.label} htmlFor="magic-email">Email</label>
                  <input className="auth-input" style={s.input} id="magic-email" type="email" required placeholder="you@example.com"
                    value={email} onChange={(e) => setEmail(e.target.value)} />
                  <button className="auth-cta" type="submit" disabled={busy} style={s.cta}>
                    {busy ? "Sending link…" : "Email me a sign-in link"}
                  </button>
                  <button type="button" onClick={() => setMagicMode(false)}
                    style={{ display: "block", width: "100%", textAlign: "center", background: "none", border: "none", color: "#888888", cursor: "pointer", fontSize: 12, marginBottom: 18 }}>
                    Use password instead
                  </button>
                </form>
              )
            )}

            {/* ── SIGN UP ── */}
            {isSignUp && (
              <form onSubmit={handleSignUp}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 0 }}>
                  <div>
                    <label style={s.label} htmlFor="signup-first">Legal first name</label>
                    <input className="auth-input" style={s.input} id="signup-first" required maxLength={100}
                      value={legalFirstName} onChange={(e) => setLegalFirstName(e.target.value)} placeholder="First" />
                  </div>
                  <div>
                    <label style={s.label} htmlFor="signup-last">Legal last name</label>
                    <input className="auth-input" style={s.input} id="signup-last" required maxLength={100}
                      value={legalLastName} onChange={(e) => setLegalLastName(e.target.value)} placeholder="Last" />
                  </div>
                </div>
                <p style={{ fontSize: 11, color: "#aaaaaa", lineHeight: 1.5, marginTop: -8, marginBottom: 16 }}>
                  Must match your government ID for verification. Used only for KYC — never shown publicly.
                </p>

                <label style={s.label} htmlFor="signup-email">Email</label>
                <input className="auth-input" style={s.input} id="signup-email" type="email" required placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} />

                <label style={s.label} htmlFor="signup-pw">Password</label>
                <input className="auth-input" style={s.input} id="signup-pw" type="password" required minLength={8} placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)} />

                <label style={s.label} htmlFor="signup-dob">Date of birth (must be {MIN_AGE}+)</label>
                <input className="auth-input" style={s.input} id="signup-dob" type="date" required
                  value={dob} onChange={(e) => setDob(e.target.value)} />

                <button className="auth-cta" type="submit" disabled={busy} style={s.cta}>
                  {busy ? "Creating account…" : "Create account"}
                </button>
              </form>
            )}

            {/* ── Divider ── */}
            <div style={s.divider}>
              <div style={s.dividerLine} />
              <span style={s.dividerText}>or</span>
              <div style={s.dividerLine} />
            </div>

            {/* ── Social buttons ── */}
            <div style={s.socialGrid}>
              <button className="auth-social" style={s.socialBtn} type="button" onClick={() => handleOAuth("google")} disabled={busy}>
                Google
              </button>
              <button className="auth-social" style={s.socialBtn} type="button" onClick={() => handleOAuth("apple")} disabled={busy}>
                Apple
              </button>
            </div>

            {phoneAuthEnabled && (
              <button type="button" onClick={() => setPhoneOpen(true)} disabled={busy}
                className="auth-social" style={{ ...s.socialBtn, width: "100%", marginBottom: 24 }}>
                Continue with Phone
              </button>
            )}

            {/* ── Legal ── */}
            <p style={s.legal}>
              18+ · Operated by Young's Digital Atelier LLC ·{" "}
              <Link to="/terms" style={s.legalLink}>Terms</Link> ·{" "}
              <Link to="/privacy" style={s.legalLink}>Privacy Policy</Link> ·{" "}
              <Link to="/cookies" style={s.legalLink}>Cookie Policy</Link> ·{" "}
              <Link to="/ai-policy" style={s.legalLink}>AI Use Policy</Link>
              <br />
              Safety features (Crisis Resources, Emergency Button) are free on every tier in every app.
            </p>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="auth-right-panel" style={s.right}>
          <WaveCanvas />
          <div style={s.pills}>
            {COMMUNITIES.map((c) => (
              <span key={c.name} style={s.pill}>
                <span style={s.dot(c.color)} />
                {c.name}
              </span>
            ))}
          </div>
          <p style={s.rightQuote as React.CSSProperties}>
            "Identity-first connection built with safety, dignity, and real community at the center."
          </p>
          <p style={s.rightAttrib as React.CSSProperties}>— wavlynq</p>
        </div>
      </div>

      <PhoneAuthDialog open={phoneOpen} onOpenChange={setPhoneOpen} />
      <BrandPreviewPanel next={next} />
      {import.meta.env.DEV && <WaveDebugPanel />}
    </>
  );
};

/* ── Wave debug/tuning panel (dev only — toggle with 🎛 button) ── */
const WaveDebugPanel = () => {
  const [open, setOpen] = useState(false);
  const [opacity, setOpacity] = useState(waveTuning.opacity);
  const [speedMul, setSpeedMul] = useState(waveTuning.speedMul);
  const [freqMul, setFreqMul] = useState(waveTuning.freqMul);

  const update = (key: keyof typeof waveTuning, val: number, setter: (v: number) => void) => {
    waveTuning[key] = val;
    setter(val);
  };

  const reset = () => {
    Object.assign(waveTuning, defaultTuning);
    setOpacity(defaultTuning.opacity);
    setSpeedMul(defaultTuning.speedMul);
    setFreqMul(defaultTuning.freqMul);
  };

  const ps: React.CSSProperties = {
    position: "fixed", bottom: 16, left: 16, zIndex: 9999,
    fontFamily: "'DM Sans', sans-serif", fontSize: 12,
  };
  const panel: React.CSSProperties = {
    background: "#1a1824ee", color: "#e8e4de", borderRadius: 12,
    padding: "14px 18px", width: 240, backdropFilter: "blur(12px)",
    boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
  };
  const row: React.CSSProperties = { marginBottom: 10 };
  const lbl: React.CSSProperties = { display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 11, color: "#aaa" };
  const slider: React.CSSProperties = { width: "100%", accentColor: "#7c5fe6", cursor: "pointer" };
  const btn: React.CSSProperties = {
    width: 36, height: 36, borderRadius: "50%", border: "none",
    background: "#1a1824cc", color: "#fff", fontSize: 18,
    cursor: "pointer", backdropFilter: "blur(8px)",
    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
  };

  return (
    <div style={ps}>
      {open && (
        <div style={{ ...panel, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>🎛 Wave Tuning</span>
            <button onClick={reset} style={{ background: "none", border: "none", color: "#7c5fe6", cursor: "pointer", fontSize: 11 }}>Reset</button>
          </div>
          <div style={row}>
            <div style={lbl}><span>Opacity</span><span>{opacity.toFixed(2)}</span></div>
            <input type="range" min="0.1" max="1" step="0.05" value={opacity}
              onChange={(e) => update("opacity", +e.target.value, setOpacity)} style={slider} />
          </div>
          <div style={row}>
            <div style={lbl}><span>Speed ×</span><span>{speedMul.toFixed(2)}</span></div>
            <input type="range" min="0" max="3" step="0.1" value={speedMul}
              onChange={(e) => update("speedMul", +e.target.value, setSpeedMul)} style={slider} />
          </div>
          <div style={row}>
            <div style={lbl}><span>Frequency ×</span><span>{freqMul.toFixed(2)}</span></div>
            <input type="range" min="0.2" max="3" step="0.1" value={freqMul}
              onChange={(e) => update("freqMul", +e.target.value, setFreqMul)} style={slider} />
          </div>
        </div>
      )}
      <button onClick={() => setOpen(!open)} style={btn} title="Wave tuning">🎛</button>
    </div>
  );
};

export default Auth;
